import { SPEC_FILE_NAME, STAGED_FILES_SNAPSHOT_ID } from "@/constants";
import { useActiveSnapshotId } from "@/lib/hooks/useActiveSnapshot";
import { useFilesystemStore } from "@/state/stores";
import { BrainIcon } from "lucide-react";
import { useLayoutEffect, useRef } from "react";
import { useStickToBottom } from "use-stick-to-bottom";
import { useShallow } from "zustand/react/shallow";
import { MarkdownFileView } from "../../SnapshotId";

export function SpecViewer() {
  const snapshotId = useActiveSnapshotId();
  const spec = useFilesystemStore(
    useShallow((state) => {
      if (snapshotId === STAGED_FILES_SNAPSHOT_ID) {
        const spec = state.stagedFiles.find(
          (file) => file.path === SPEC_FILE_NAME,
        );
        return {
          ...spec,
          content: spec?.content ?? "",
          status: spec?.status ?? "completed",
        };
      }

      const spec = state.filesBySnapshotId[snapshotId]?.find(
        (file) => file.path === SPEC_FILE_NAME,
      );

      return {
        ...spec,
        content: spec?.content ?? "",
        status: spec?.status ?? "completed",
      };
    }),
  );

  const isProcessing = spec?.status === "processing";
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: isProcessing && "instant",
  });

  const processingRef = useRef(isProcessing);

  useLayoutEffect(() => {
    const processingChanged = processingRef.current !== isProcessing;
    processingRef.current = isProcessing;
    if (isProcessing || processingChanged) {
      scrollToBottom("instant");
    }
  }, [isProcessing, scrollToBottom]);

  if (!spec) {
    return (
      <div className="h-full flex flex-col overflow-y-auto bg-background">
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="text-muted-foreground flex gap-2 items-center">
              <BrainIcon />
              No spec available for this version
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background" ref={scrollRef}>
      <div className="flex flex-col" ref={contentRef}>
        <MarkdownFileView key={snapshotId} file={spec} />
      </div>
    </div>
  );
}
